import type { MutableRefObject } from 'react';
import { useCallback, useEffect, useRef, useState } from 'react';

import { getWindow } from '@change-corgi/core/window';

import { usePaypal } from 'src/app/shared/hooks/payments';
import type { WithTokenParams } from 'src/app/shared/types';
import { isLoaded } from 'src/app/shared/utils/async';
import type { PaymentMethodSaveOptions, PaymentType, VenmoClient } from 'src/app/shared/utils/payments';

type Result = ModelHookResult<
	{
		ready: boolean;
		ref: MutableRefObject<HTMLButtonElement | null>;
	},
	{
		onClick: () => Promise<void>;
	}
>;

export type HookProps = {
	email: string;
	paymentMethodSaveOptions: PaymentMethodSaveOptions;
	beforeToken: (paymentType: PaymentType) => Promise<boolean>;
	onTokenError: (paymentType: PaymentType, err: Error) => void;
	onTokenInvalid: (paymentType: PaymentType, err: Error) => void;
	validate?: (paymentType: PaymentType) => Promise<boolean>;
	withToken: (params: WithTokenParams) => Promise<void>;
	prePaymentChecks?: (paymentType: PaymentType) => Promise<boolean>;
};

export function useVenmoButton({ paymentMethodSaveOptions, onTokenError, withToken }: HookProps): Result {
	const ref = useRef<HTMLButtonElement | null>(null);
	const paypalState = usePaypal();

	const [venmo, setVenmo] = useState<VenmoClient | null>(null);

	useEffect(() => {
		(async function init() {
			if (isLoaded(paypalState)) {
				const venmoInstance = await getWindow().braintree.venmo.create({
					client: paypalState.braintreeClientInstance,
					paymentMethodUsage: paymentMethodSaveOptions.shouldSavePaymentMethod ? 'multi_use' : 'single_use',
				});
				setVenmo(venmoInstance);
			}
		})();
	}, [paypalState, paymentMethodSaveOptions]);

	const onClick = useCallback(async () => {
		if (!venmo) return Promise.resolve();
		try {
			const { nonce } = await venmo.tokenize();
			void withToken({ paymentType: 'venmo', token: nonce });
		} catch (err) {
			// eslint-disable-next-line @typescript-eslint/consistent-type-assertions
			void onTokenError('venmo', err as Error);
		}
		return Promise.resolve();
	}, [onTokenError, venmo, withToken]);

	return {
		data: {
			ref,
			ready: isLoaded(paypalState) && !!venmo,
		},
		actions: {
			onClick,
		},
	};
}
